/**
 * Phase 27.39 — Team Member Profile.
 *
 * Displays and edits a non-agent staff member's profile:
 *   • Header with avatar (upload/remove), name, roles, agency.
 *   • Basic info form (phone, DOB, address, notes for KR).
 *   • Google Calendar connect prompt (uses the existing per-user OAuth).
 *   • SMS preference block (uses the existing per-user consent flow).
 *   • Internal SMS composer for KR to send test / support messages.
 *
 * Permissions mirror the backend:
 *   • KR admins can edit everything.
 *   • Users can edit their own profile (except role/agency/notes).
 *   • Everyone else — read-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Camera, Save, Loader2, Mail, Phone, Cake, MapPin,
  Shield, StickyNote, User, Building2, Trash2, Upload, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { UserAvatar } from '@/components/users/UserAvatar';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, getErrorMessage, formatPhone, formatPhoneInput } from '@/lib/utils';
import { teamAPI } from '@/lib/api';
import AgentSmsPreference from '@/components/notifications/AgentSmsPreference';
import { GoogleCalendarConnectPrompt } from '@/components/integrations/GoogleCalendarConnectPrompt';

const ROLE_LABEL = {
  KR_SuperAdmin: 'KR Super Admin',
  KR_AdminOps: 'KR Admin',
  Accounting: 'Accounting',
  Agency_User: 'Agency Admin',
  Agency_Principal: 'Agency Principal',
};

function RoleBadges({ roles }) {
  if (!roles?.length) return <span className="text-xs text-slate-400">No roles assigned</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {roles.map((r, i) => (
        <Badge
          key={i}
          variant="outline"
          className="text-[10px] bg-sky-50 text-sky-800 border-sky-200"
        >
          <Shield className="w-3 h-3 mr-1" />
          {ROLE_LABEL[r.role] || r.role}
          {r.agency_name && <span className="ml-1 opacity-70">· {r.agency_name}</span>}
        </Badge>
      ))}
    </div>
  );
}

export default function TeamMemberProfilePage() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, refreshUser } = useAuth();
  const isKR = isKRRole(currentUser);
  const isSelf = currentUser?.user_id === user_id;
  const canEdit = isKR || isSelf;

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await teamAPI.get(user_id);
      setMember(r.data);
      setForm({
        first_name: r.data.first_name || '',
        last_name: r.data.last_name || '',
        phone: r.data.phone || '',
        date_of_birth: r.data.date_of_birth || '',
        address_line1: r.data.address_line1 || '',
        address_line2: r.data.address_line2 || '',
        city: r.data.city || '',
        state: r.data.state || '',
        zip: r.data.zip || '',
        notes_internal: r.data.notes_internal || '',
      });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load profile'));
    } finally {
      setLoading(false);
    }
  }, [user_id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isKR) delete payload.notes_internal;
      await teamAPI.update(user_id, payload);
      toast.success('Profile updated');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await teamAPI.uploadPhoto(user_id, file);
      toast.success('Photo updated');
      await load();
      // Phase 27.39.3 — When editing your own profile, refresh the
      // auth context so the sidebar avatar picks up the new photo.
      if (isSelf && refreshUser) await refreshUser();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to upload photo'));
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoRemove = async () => {
    try {
      await teamAPI.deletePhoto(user_id);
      toast.success('Photo removed');
      await load();
      if (isSelf && refreshUser) await refreshUser();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to remove photo'));
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (!member) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-center text-slate-500">
        Profile not found.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <button
        onClick={() => navigate('/team')}
        className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Team
      </button>

      {/* Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="relative">
              <UserAvatar
                user={member}
                size="w-20 h-20"
                rounded="full"
                textSize="text-lg"
                testId="team-profile-avatar"
              />
              {canEdit && (
                <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center cursor-pointer shadow-md border-2 border-white">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                    data-testid="team-profile-photo-upload"
                  />
                </label>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-heading font-semibold" style={{ color: 'var(--kr-navy-900)' }}>
                {member.display_name}
              </h1>
              <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                <Mail className="w-3.5 h-3.5" />
                {member.email}
              </div>
              {member.phone && (
                <div className="flex items-center gap-2 text-sm text-slate-500 mt-0.5">
                  <Phone className="w-3.5 h-3.5" />
                  {formatPhone(member.phone)}
                </div>
              )}
              <div className="mt-3">
                <RoleBadges roles={member.roles} />
              </div>
              {canEdit && member.photo_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-6 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  onClick={handlePhotoRemove}
                  data-testid="team-profile-photo-remove"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Remove photo
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                disabled={!canEdit}
                data-testid="team-profile-first-name"
              />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                disabled={!canEdit}
                data-testid="team-profile-last-name"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Phone</Label>
              <Input
                value={formatPhoneInput(form.phone)}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                placeholder="+1 555 555 5555"
                disabled={!canEdit}
                data-testid="team-profile-phone"
              />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={form.date_of_birth}
                onChange={(e) => setForm((p) => ({ ...p, date_of_birth: e.target.value }))}
                disabled={!canEdit}
                data-testid="team-profile-dob"
              />
            </div>
          </div>
          <Separator />
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Address
          </div>
          <div>
            <Label>Address line 1</Label>
            <Input
              value={form.address_line1}
              onChange={(e) => setForm((p) => ({ ...p, address_line1: e.target.value }))}
              disabled={!canEdit}
              data-testid="team-profile-address1"
            />
          </div>
          <div>
            <Label>Address line 2 (optional)</Label>
            <Input
              value={form.address_line2}
              onChange={(e) => setForm((p) => ({ ...p, address_line2: e.target.value }))}
              disabled={!canEdit}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>City</Label>
              <Input
                value={form.city}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>State</Label>
              <Input
                value={form.state}
                onChange={(e) => setForm((p) => ({ ...p, state: e.target.value.toUpperCase() }))}
                maxLength={2}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label>Zip</Label>
              <Input
                value={form.zip}
                onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
                maxLength={10}
                disabled={!canEdit}
              />
            </div>
          </div>
          {isKR && (
            <>
              <Separator />
              <div>
                <Label className="flex items-center gap-1.5">
                  <StickyNote className="w-3.5 h-3.5" /> Internal notes (KR only)
                </Label>
                <Textarea
                  rows={3}
                  value={form.notes_internal}
                  onChange={(e) => setForm((p) => ({ ...p, notes_internal: e.target.value }))}
                  placeholder="Notes visible only to KR Admins…"
                  data-testid="team-profile-notes"
                />
              </div>
            </>
          )}
          {canEdit && (
            <div className="flex justify-end pt-2">
              <Button onClick={save} disabled={saving} data-testid="team-profile-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save changes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Calendar (visible only when viewing your own profile so
          the OAuth flow lands on the right user session). */}
      {isSelf && (
        <div>
          <GoogleCalendarConnectPrompt context="team" />
        </div>
      )}

      {/* SMS preference — visible only on the user's own profile since
          it drives their personal opt-in / consent. */}
      {isSelf && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading">Internal SMS</CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Receive system SMS (events, tickets, broadcasts). This profile
              CANNOT send SMS to Leads — that permission is restricted to
              agents with explicit access.
            </p>
          </CardHeader>
          <CardContent>
            <AgentSmsPreference />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
